/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as OnlineDuck from 'store/ducks/offline';
import { Modal } from 'ui/components';
import translate from 'services/i18n/translate';

const OfflineTracker: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // Connection Status Duck
  const onlineStatus = useSelector(OnlineDuck.Selectors.isOnline);
  const [isOnline, setIsonline] = useState<boolean>(true);

  // see https://gist.github.com/jpsilvashy/5725579
  // Verify if a file on the webserver is reachable
  const hostReachable = () => {
    // Handle IE and more capable browsers
    const xhr = new ((window as any).ActiveXObject || XMLHttpRequest)('Microsoft.XMLHTTP');
    const { hostname, port } = window.location;
    const url = port ? `${hostname}:${port}` : hostname;

    // Open new request as a HEAD to the root hostname with a random param to bypass the cache
    xhr.open('HEAD', `//${url}/favicon.ico?rand=${Math.floor((1 + Math.random()) * 0x10000)}`, false);

    // Issue request and handle response
    try {
      xhr.send();
      console.log('XHR STATUS: ', xhr.status);
      if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) {
        // setIsonline(true);
      }
      return true;
    } catch (error) {
      // setIsonline(false);
      return false;
    }
  };

  // Set to local state the Duck value
  useEffect(() => {
    setIsonline(onlineStatus);
  }, [onlineStatus]);

  // State - Dialog for Offline mode
  const [openOfflineDialog, setOpenOfflineDialog] = useState<boolean>(false);

  // Trigger Offline Modal Dialog when there's no Internet connection and on Location change
  useEffect(() => {
    if (!isOnline) {
      setOpenOfflineDialog(true);
    }
    if (isOnline) {
      setOpenOfflineDialog(false);
    }
    return () => {
      setOpenOfflineDialog(true);
    };
  }, [isOnline, location]);

  // Effect to provide listners for Offline mode
  useEffect(() => {
    // Event to verify Online Connection Status
    function changeStatus() {
      hostReachable();
      dispatch(OnlineDuck.ActionCreators.onlineStatusChanged(navigator.onLine));
    }
    window.addEventListener('load', changeStatus);
    window.addEventListener('online', changeStatus);
    window.addEventListener('offline', changeStatus);

    return () => {
      // Remove listeners for Online Connection Status
      window.removeEventListener('online', changeStatus);
      window.removeEventListener('offline', changeStatus);
      window.addEventListener('load', changeStatus);
    };
  }, [dispatch, isOnline]);

  // Close Offline Dialog
  const onCloseOfflineDialog = () => {
    setOpenOfflineDialog(false);
  };

  return (
    <Modal
      setIsShowing={setOpenOfflineDialog}
      isShowing={openOfflineDialog}
      title={translate.modalOfflineHeader}
      message={translate.modalOfflineMessage}
      buttons={[{ id: 'ok', text: translate.modalButtonOk, onClick: onCloseOfflineDialog }]}
    />
  );
};

export default OfflineTracker;
